import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { MapPin, Clock, Loader2, Globe } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://www.mypanther.fr/web/image/website/1/logo/My%20Panther?unique=501ee63";

const LANGS = {
  fr: { label: 'FR', flag: 'https://flagcdn.com/w20/fr.png' },
  en: { label: 'EN', flag: 'https://flagcdn.com/w20/gb.png' },
  vi: { label: 'VI', flag: 'https://flagcdn.com/w20/vn.png' },
};

const T = {
  fr: {
    title: 'Suivi de colis',
    subtitle: 'Panther Delivery Tracking',
    tracking_number: 'Numéro de suivi',
    no_tracking: 'Aucun numéro de suivi fourni.',
    loading: 'Chargement...',
    delivered_on: 'Livré le',
    estimated: 'Estimation de livraison le :',
    history: 'Historique',
    events: 'événements',
    steps: ['Pris en charge', 'En transit', 'Arrivé destination', 'Livré'],
    overall: { 'Pris en charge': 'Pris en charge', 'En transit': 'En transit', 'Arrivé destination': 'Arrivé destination', 'Livré': 'Livré' },
    powered: 'Powered by Panther',
    locale: 'fr-FR',
  },
  en: {
    title: 'Package Tracking',
    subtitle: 'Panther Delivery Tracking',
    tracking_number: 'Tracking number',
    no_tracking: 'No tracking number provided.',
    loading: 'Loading...',
    delivered_on: 'Delivered on',
    estimated: 'Estimated delivery:',
    history: 'History',
    events: 'events',
    steps: ['Picked up', 'In transit', 'Arrived destination', 'Delivered'],
    overall: { 'Pris en charge': 'Picked up', 'En transit': 'In transit', 'Arrivé destination': 'Arrived destination', 'Livré': 'Delivered' },
    powered: 'Powered by Panther',
    locale: 'en-GB',
  },
  vi: {
    title: 'Theo dõi bưu kiện',
    subtitle: 'Panther Delivery Tracking',
    tracking_number: 'Mã theo dõi',
    no_tracking: 'Không có mã theo dõi.',
    loading: 'Đang tải...',
    delivered_on: 'Đã giao ngày',
    estimated: 'Dự kiến giao hàng ngày:',
    history: 'Lịch sử',
    events: 'sự kiện',
    steps: ['Đã nhận', 'Đang vận chuyển', 'Đến nơi', 'Đã giao'],
    overall: { 'Pris en charge': 'Đã nhận', 'En transit': 'Đang vận chuyển', 'Arrivé destination': 'Đến nơi', 'Livré': 'Đã giao' },
    powered: 'Powered by Panther',
    locale: 'vi-VN',
  }
};

// Status translations for events (fr status -> en/vi)
const STATUS_EN = {
  "Etiquette créée": "Label created",
  "Colis reçu": "Package received",
  "Traitement douane départ": "Origin customs processing",
  "En transit": "In transit",
  "Parti": "Departed",
  "Arrivé aéroport transit": "Arrived transit airport",
  "Arrivé aéroport destination": "Arrived destination airport",
  "Traitement douane destination": "Destination customs processing",
  "Dédouané à destination": "Customs released",
  "Transmis au transporteur local": "Forwarded to local carrier",
  "Livré au destinataire": "Delivered to recipient",
  "Chargé sur le véhicule de livraison": "Loaded onto delivery vehicle",
  "En préparation au dépôt de livraison": "Preparing at delivery depot",
  "Arrivé au pays de destination": "Arrived in destination country",
  "Parti du centre colis d'import du pays de destination": "Left import parcel center",
  "Arrivé au centre colis": "Arrived at parcel center",
  "Traité au centre d'export international": "Processed at export center",
  "Traité au centre colis d'origine": "Processed at origin parcel center",
  "En route vers le pays de destination": "En route to destination country",
  "Données d'envoi transmises à DHL": "Shipment data sent to DHL",
  "Dédouanement": "Customs clearance",
  "Dédouané": "Customs cleared",
  "En cours de livraison": "Out for delivery",
  "Livré": "Delivered",
};

const STATUS_VI = {
  "Etiquette créée": "Đã tạo nhãn",
  "Colis reçu": "Đã nhận kiện hàng",
  "Traitement douane départ": "Xử lý hải quan xuất",
  "En transit": "Đang vận chuyển",
  "Parti": "Đã khởi hành",
  "Arrivé aéroport transit": "Đến sân bay trung chuyển",
  "Arrivé aéroport destination": "Đến sân bay đích",
  "Traitement douane destination": "Xử lý hải quan đích",
  "Dédouané à destination": "Đã thông quan",
  "Transmis au transporteur local": "Chuyển cho đơn vị vận chuyển nội địa",
  "Livré au destinataire": "Đã giao cho người nhận",
  "Chargé sur le véhicule de livraison": "Đã xếp lên xe giao hàng",
  "En préparation au dépôt de livraison": "Đang chuẩn bị tại kho giao",
  "Arrivé au pays de destination": "Đã đến quốc gia đích",
  "Parti du centre colis d'import du pays de destination": "Rời trung tâm nhập khẩu",
  "Arrivé au centre colis": "Đến trung tâm bưu kiện",
  "Traité au centre d'export international": "Xử lý tại trung tâm xuất quốc tế",
  "Traité au centre colis d'origine": "Xử lý tại trung tâm gốc",
  "En route vers le pays de destination": "Trên đường đến quốc gia đích",
  "Données d'envoi transmises à DHL": "Đã gửi dữ liệu cho DHL",
  "Dédouanement": "Thông quan",
  "Dédouané": "Đã thông quan",
  "En cours de livraison": "Đang giao hàng",
  "Livré": "Đã giao",
};

const OVERALL_COLORS = {
  'Livré': 'bg-emerald-600 text-white',
  'Arrivé destination': 'bg-blue-600 text-white',
  'En transit': 'bg-amber-500 text-white',
  'Pris en charge': 'bg-slate-500 text-white',
};

function translateEvent(status, lang) {
  if (lang === 'fr') return status;
  const map = lang === 'en' ? STATUS_EN : STATUS_VI;
  return map[status] || status;
}

export default function PublicTracking() {
  const [searchParams] = useSearchParams();
  const trackingNumber = searchParams.get('n') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState('fr');

  const t = T[lang];

  useEffect(() => {
    if (!trackingNumber) return;
    setLoading(true);
    axios.get(`${API}/api/public/tracking/${trackingNumber}`)
      .then(res => setData(res.data))
      .catch(e => setError(e.response?.data?.detail || 'Erreur'))
      .finally(() => setLoading(false));
  }, [trackingNumber]);

  const overallLabel = t.overall[data?.overall_status] || data?.overall_status || '';
  const statusMap = { 'Pris en charge': 0, 'En transit': 1, 'Arrivé destination': 2, 'Livré': 3 };
  const currentIdx = statusMap[data?.overall_status] ?? 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-xl mx-auto px-4 flex items-center gap-3">
          <img src={LOGO_URL} alt="Panther" className="h-10" />
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-900">{t.title}</h1>
            <p className="text-xs text-slate-500">{t.subtitle}</p>
          </div>
          <div className="flex gap-1">
            {Object.entries(LANGS).map(([key, { label, flag }]) => (
              <button
                key={key}
                data-testid={`lang-${key}`}
                onClick={() => setLang(key)}
                className={`px-2 py-1 rounded text-xs font-medium transition-all flex items-center gap-1 ${
                  lang === key
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <img src={flag} alt={label} className="w-4 h-3 object-cover rounded-sm" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8">
        {!trackingNumber && (
          <p className="text-center text-slate-500 py-12">{t.no_tracking}</p>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={28} className="animate-spin text-slate-400" />
            <span className="ml-3 text-slate-500">{t.loading}</span>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {data && !loading && (
          <div className="space-y-6">
            {/* Header card */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">{t.tracking_number}</p>
                  <p className="text-xl font-bold text-slate-900 mt-1">{data.tracking_number}</p>
                </div>
                <Badge className={`text-sm px-4 py-1.5 ${OVERALL_COLORS[data.overall_status] || 'bg-slate-500 text-white'}`}>
                  {overallLabel}
                </Badge>
              </div>
              {data.delivered_date && (
                <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
                  <p className="text-sm font-semibold text-emerald-800">
                    {t.delivered_on} {new Date(data.delivered_date).toLocaleDateString(t.locale, { day: '2-digit', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              )}
              {data.events?.length > 0 && (() => {
                const firstDate = data.first_event_date || (data.events?.length > 0 ? data.events[data.events.length - 1].date : null);
                if (!firstDate) return null;
                const estimated = new Date(new Date(firstDate).getTime() + 15 * 24 * 60 * 60 * 1000);
                return (
                  <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                    <p className="text-sm font-bold text-slate-800">
                      {t.estimated} {estimated.toLocaleDateString(t.locale, { day: '2-digit', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* Progress Bar */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center justify-between relative">
                <div className="absolute top-4 left-6 right-6 h-1 bg-slate-200 rounded-full" />
                <div
                  className="absolute top-4 left-6 h-1 bg-emerald-500 rounded-full transition-all duration-700"
                  style={{ width: `calc(${(currentIdx / 3) * 100}% - 48px)` }}
                />
                {t.steps.map((label, i) => (
                  <div key={i} className="flex flex-col items-center relative z-10" style={{ flex: 1 }}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                      i <= currentIdx
                        ? 'bg-emerald-500 border-emerald-500 text-white'
                        : 'bg-white border-slate-300 text-slate-400'
                    }`}>
                      {i < currentIdx ? '✓' : i + 1}
                    </div>
                    <span className={`text-[11px] mt-2 text-center font-medium ${
                      i <= currentIdx ? 'text-emerald-700' : 'text-slate-400'
                    }`}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Events timeline */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-4">
                {t.history} ({data.events?.length || 0} {t.events})
              </h2>
              <div className="relative">
                {data.events?.map((event, i) => (
                  <div key={`${event.date}-${i}`} className="flex gap-3 pb-5 last:pb-0">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${i === 0 ? 'bg-emerald-500 ring-2 ring-emerald-200' : 'bg-slate-300'}`} />
                      {i < data.events.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 mt-1" />}
                    </div>
                    <div className="flex-1 pb-1">
                      <p className={`text-sm font-semibold ${i === 0 ? 'text-slate-900' : 'text-slate-600'}`}>
                        {translateEvent(event.status, lang)}
                      </p>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {event.location && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <MapPin size={11} /> {event.location}
                          </span>
                        )}
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock size={11} />
                          {new Date(event.date).toLocaleDateString(t.locale, { day: '2-digit', month: 'short', year: 'numeric' })}
                          {' '}
                          {new Date(event.date).toLocaleTimeString(t.locale, { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-center text-xs text-slate-400">{t.powered}</p>
          </div>
        )}
      </div>
    </div>
  );
}
